import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import {
  Button,
  Col,
  DatePicker,
  Row,
  Select,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';

import { useCreateFreightLaneCostMutation } from '~/modules/transportation/hooks/mutations/useCreateFreightLaneCostMutation';
import { useUpdateFreightLaneCostMutation } from '~/modules/transportation/hooks/mutations/useUpdateFreightLaneCostMutation';
import { FreightLaneCostRecord } from '~/modules/transportation/types';
import { maybeParseCalendarDate } from '~/shared/utils/dateTime';

type FreightLaneCostFormProps = {
  freightLaneId: string;
  cost?: FreightLaneCostRecord;
  onSuccess: () => void;
  onCancel: () => void;
};

const FUEL_SURCHARGE_MODE_OPTIONS = [
  { key: 'PERCENTAGE', label: 'Percentage' },
  { key: 'FLAT', label: 'Flat' },
  { key: 'PER_MILE', label: 'Per Mile' },
];

export const FreightLaneCostForm = ({
  freightLaneId,
  cost,
  onSuccess,
  onCancel,
}: FreightLaneCostFormProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [startDate, setStartDate] = React.useState<CalendarDate | null>(
    maybeParseCalendarDate(cost?.startDate) ?? null,
  );
  const [endDate, setEndDate] = React.useState<CalendarDate | null>(
    maybeParseCalendarDate(cost?.endDate) ?? null,
  );
  const [rateFormula, setRateFormula] = React.useState(cost?.rateFormula ?? '');
  const [chargebackFormula, setChargebackFormula] = React.useState(
    cost?.chargebackFormula ?? '',
  );
  const [fuelSurchargeFormula, setFuelSurchargeFormula] = React.useState(
    cost?.fuelSurchargeFormula ?? '',
  );
  const [fuelSurchargeMode, setFuelSurchargeMode] = React.useState(
    cost?.fuelSurchargeMode ?? '',
  );
  const [minimumChargeAmount, setMinimumChargeAmount] = React.useState(
    cost?.minimumChargeAmount != null ? String(cost.minimumChargeAmount) : '',
  );
  const [currency, setCurrency] = React.useState(cost?.currency ?? 'USD');

  const isEditing = !!cost;

  const { mutateAsync: createCost, isPending: isCreatePending } =
    useCreateFreightLaneCostMutation();
  const { mutateAsync: updateCost, isPending: isUpdatePending } =
    useUpdateFreightLaneCostMutation();

  const isPending = isCreatePending || isUpdatePending;

  const handleSave = async () => {
    if (!startDate || !rateFormula || !currency) return;

    const startDateStr = startDate.toString();
    const endDateStr = endDate?.toString();

    try {
      if (isEditing) {
        await updateCost({
          id: cost.id,
          startDate: startDateStr,
          endDate: endDateStr || null,
          rateFormula,
          chargebackFormula: chargebackFormula || null,
          fuelSurchargeFormula: fuelSurchargeFormula || null,
          fuelSurchargeMode: fuelSurchargeMode || null,
          minimumChargeAmount: minimumChargeAmount || null,
          currency,
        });
        toast.success(t.freightLanes.costs.messages.updateSuccess);
      } else {
        await createCost({
          freightLaneId,
          startDate: startDateStr,
          endDate: endDateStr || undefined,
          rateFormula,
          chargebackFormula: chargebackFormula || undefined,
          fuelSurchargeFormula: fuelSurchargeFormula || undefined,
          fuelSurchargeMode: fuelSurchargeMode || undefined,
          minimumChargeAmount: minimumChargeAmount || undefined,
          currency,
        });
        toast.success(t.freightLanes.costs.messages.createSuccess);
      }
      onSuccess();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.freightLanes.costs.messages.error,
      );
    }
  };

  return (
    <div className="@FreightLaneCostForm rounded-md border bg-neutral/5 p-3">
      <Col gap3>
        <div className="text-sm font-medium">
          {isEditing
            ? t.freightLanes.costs.form.titleEdit
            : t.freightLanes.costs.form.titleCreate}
        </div>

        <Row gap3>
          <DatePicker
            label={t.freightLanes.costs.fields.startDate}
            value={startDate}
            onChange={setStartDate}
            isRequired
            className="flex-1"
          />
          <DatePicker
            label={t.freightLanes.costs.fields.endDate}
            value={endDate}
            onChange={setEndDate}
            className="flex-1"
          />
        </Row>

        <TextField
          label={t.freightLanes.costs.fields.rateFormula}
          value={rateFormula}
          onChange={setRateFormula}
          isRequired
        />

        <TextField
          label={t.freightLanes.costs.fields.chargebackFormula}
          value={chargebackFormula}
          onChange={setChargebackFormula}
        />

        <Row gap3>
          <TextField
            label={t.freightLanes.costs.fields.fuelSurchargeFormula}
            value={fuelSurchargeFormula}
            onChange={setFuelSurchargeFormula}
            className="flex-1"
          />
          <Select
            label={t.freightLanes.costs.fields.fuelSurchargeMode}
            value={fuelSurchargeMode || undefined}
            onChange={(value) => setFuelSurchargeMode(value ?? '')}
            options={FUEL_SURCHARGE_MODE_OPTIONS}
            className="flex-1"
          />
        </Row>

        <Row gap3>
          <TextField
            label={t.freightLanes.costs.fields.minimumCharge}
            value={minimumChargeAmount}
            onChange={setMinimumChargeAmount}
            className="flex-1"
          />
          <TextField
            label={t.freightLanes.costs.fields.currency}
            value={currency}
            onChange={setCurrency}
            isRequired
            className="flex-1"
          />
        </Row>

        <Row gap2 className="justify-end">
          <Button variant="outline" size="small" onPress={onCancel}>
            {t.freightLanes.costs.form.cancel}
          </Button>
          <Button
            color="primary"
            variant="solid"
            size="small"
            onPress={handleSave}
            isPending={isPending}
            isLoading={isPending}
          >
            {isEditing
              ? t.freightLanes.costs.form.save
              : t.freightLanes.costs.form.create}
          </Button>
        </Row>
      </Col>
    </div>
  );
};
